import { useEffect, useRef, useState } from 'react'

type DashboardFilterSubset = {
  fromDate?: string
  toDate?: string
  graphGranularityDateRange?: { from: string; to: string } | null
  location?: string
  program?: string
  userEmail?: string
  patientMrn?: string
  measures?: string[]
  selectedSeverity?: 1 | 2 | 3 | null
}

type UseResetDashboardPageOptions = {
  filters: DashboardFilterSubset
  setCurrentPage: (page: number) => void
  resetKey?: string | number | null
}

export const useResetDashboardPage = ({
  filters,
  setCurrentPage,
  resetKey,
}: UseResetDashboardPageOptions) => {
  const measuresKey = filters.measures?.join(',')
  const latestSetCurrentPage = useRef(setCurrentPage)

  useEffect(() => {
    latestSetCurrentPage.current = setCurrentPage
  }, [setCurrentPage])

  useEffect(() => {
    latestSetCurrentPage.current(1)
  }, [
    filters.fromDate,
    filters.toDate,
    filters.graphGranularityDateRange?.from,
    filters.graphGranularityDateRange?.to,
    filters.location,
    filters.program,
    filters.userEmail,
    filters.patientMrn,
    measuresKey,
    filters.selectedSeverity,
    resetKey,
  ])
}

export const useDashboardTableSort = <TField extends string>(
  initialField: TField,
  initialOrder: 'asc' | 'desc' = 'asc'
) => {
  const [orderBy, setOrderBy] = useState<TField>(initialField)
  const [order, setOrder] = useState<'asc' | 'desc'>(initialOrder)

  const handleRequestSort = (property: TField) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  return {
    orderBy,
    order,
    handleRequestSort,
  }
}
